import React, { useMemo } from 'react';
import { Form } from 'react-final-form';
import * as T from 'prop-types';
import createDecorator from 'final-form-focus';
import { useAutoSaveDraft } from '../../commons/draftHooks';
import { PageErrorComponent } from '../../commons/PageErrorComponent';
import Disclaimer from '../../commons/Disclaimer';
import PresentationContainer from './presentation/PresentationContainer';
import GenericCheckboxInputField from '../../commons/form-fields/GenericCheckboxInputField';
import { getSlidesByCountry } from './presentation/slides/SlideUtils';

const SlidePresentationPage = (props) => (
    <div className="mt-2 max-w-6xl w-full mx-auto py-4">
        <Disclaimer>
            <p>
                All participants in D. E. Shaw&#39;s ICP must adhere to the
                rules of the program. This brief slide presentation explains
                those rules and provides other important information. Please
                review the presentation below before proceeding and confirm that
                you have read and understand the presentation.
            </p>
        </Disclaimer>
        <PresentationContainer
            slidesWatched={props.slidesWatched}
            setSlidesWatched={props.setSlidesWatched}
            onTrainingCompletion={props.onTrainingCompletion}
            hasCompletedTrainingPreviously={
                props.hasCompletedTrainingPreviously
            }
            country={props.country}
        />
        <GenericCheckboxInputField
            name="application.terms.trainingPresentationCompleted"
            label={
                'I have completed the ICP training presentation and understand the obligations of an ICP consultant.'
            }
            errorMsg={
                'Please confirm that you have reviewed the above presentation.'
            }
            required
            // disable if all slides haven't been gone through and training isn't completed already.
            disabled={
                props.slidesWatched !==
                    getSlidesByCountry(props.country).totalSlides &&
                !props.hasCompletedTrainingPreviously
            }
        />
        <br />
    </div>
);

SlidePresentationPage.propTypes = {
    hasCompletedTrainingPreviously: T.bool,
    slidesWatched: T.number,
    setSlidesWatched: T.func,
    onTrainingCompletion: T.func,
    country: T.string,
};

const Page3 = (props) => {
    // return same decorator for subsequent renders.
    const focusOnError = useMemo(() => createDecorator(), []);
    return (
        <Form
            onSubmit={(values) => {
                props.nextFn(values);
            }}
            decorators={[focusOnError]}
            initialValues={props.initialValues}
            keepDirtyOnReinitialize
            render={({
                values,
                errors,
                handleSubmit,
                submitFailed,
                submitErrors,
            }) => {
                useAutoSaveDraft(props.autoSaveCallback, values, errors);

                return (
                    <form onSubmit={handleSubmit}>
                        <SlidePresentationPage
                            hasCompletedTrainingPreviously={
                                props.hasCompletedTrainingPreviously
                            }
                            onTrainingCompletion={props.onTrainingCompletion}
                            slidesWatched={props.slidesWatched}
                            setSlidesWatched={props.setSlidesWatched}
                            country={props.country}
                        />
                        {!submitErrors && submitFailed && errors && (
                            <PageErrorComponent error={errors} />
                        )}
                        <button
                            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                            type="button" // normal button because people should be able to move backward without getting stuck on this page due to validation.
                            onClick={() => props.prevFn(values, errors)}
                            data-testid="previous-page"
                        >
                            &#60; Previous Page
                        </button>
                        <button
                            className="disabled:opacity-50 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded float-right"
                            type="submit" // button type is submit, so that it triggers form validation before calling the next page function.
                            data-testid="next-page"
                        >
                            Next Page &#62;
                        </button>
                    </form>
                );
            }}
        />
    );
};

Page3.propTypes = {
    initialValues: T.object,
    country: T.string,
    slidesWatched: T.number,
    autoSaveCallback: T.func,
    nextFn: T.func,
    prevFn: T.func,
    setSlidesWatched: T.func,
    onTrainingCompletion: T.func,
    hasCompletedTrainingPreviously: T.bool,
};

export default Page3;
